// frontend/src/pages/InvoiceEditor.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || "";

const badge = (st) => {
  const s = (st || "").toUpperCase();
  if (s === "DRAFT") return "badge bg-secondary";
  if (s === "ISSUED") return "badge bg-info text-dark";
  if (s === "SENT") return "badge bg-primary";
  if (s === "PAID") return "badge bg-success";
  if (s === "CANCELED") return "badge bg-dark";
  return "badge bg-light text-dark";
};

// ---------- helper robusti: date e numeri ----------
function toISODate(v) {
  if (!v) return null;
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const [_, d, mo, y] = m;
    return `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }
  const dt = new Date(s);
  if (!isNaN(dt.getTime())) {
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${dt.getFullYear()}-${mm}-${dd}`;
  }
  return null;
}

function formatDateTimeIT(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");

  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

function toNumber(v, def = 0) {
  if (v === null || v === undefined || v === "") return def;
  if (typeof v === "number") return isNaN(v) ? def : v;
  const s = String(v).replace(/\./g, "").replace(",", ".");
  const n = Number(s);
  return isNaN(n) ? def : n;
}
function compact(obj) {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (typeof v === "string" && v.trim() === "") return;
    out[k] = v;
  });
  return out;
}

const defaultPriceFromProduct = (p) => {
  if (!p) return 0;
  if (p.prezzo != null) return Number(p.prezzo);
  if (p.price != null) return Number(p.price);
  if (p.price_azienda != null) return Number(p.price_azienda);
  const li = Array.isArray(p.listini) ? p.listini.find((l) => l.tipo === "azienda") : null;
  if (li && li.prezzo != null) return Number(li.prezzo);
  return 0;
};
const defaultVatFromProduct = (p) => {
  if (!p) return 22;
  if (p.vat_rate != null) return Number(p.vat_rate);
  return 22;
};

const DOC_TYPES = [
  { v: "TD01", l: "Proforma" },
  { v: "TD04", l: "Nota di credito" },
];
const REGIMI = [
  { v: "RF01", l: "Ordinario" },
  { v: "RF19", l: "Forfettario (L. 190/2014)" },
];
const NATURE_IVA = [
  { v: "", l: "—" },
  { v: "N1", l: "Escluse ex art. 15" },
  { v: "N2.2", l: "Non soggette (forfettario)" },
  { v: "N4", l: "Esenti" },
];

// Opzioni Ruoli per UI (etichette friendly)
const ROLE_OPTIONS = [
  { label: "Franchising", value: "FRANCHISING" },
  { label: "Punto Vendita", value: "FILIALE" }, // mappata su PV nel payload
  { label: "Azienda", value: "AZIENDA" },
  { label: "Agente", value: "AGENTE" },
];

export default function InvoiceEditor() {
  const { id } = useParams();
  const nav = useNavigate();

  const [role, setRole] = useState("");
  const isMadre = role === "MADRE";
  const canManagePayments = ["MADRE", "SEDE", "FRANCHISING", "PV"].includes(role);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [status, setStatus] = useState("DRAFT");
  const [series, setSeries] = useState("A");
  const [number, setNumber] = useState(null);
  const [date, setDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");

  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);

  const [fatt, setFatt] = useState({
    fatt_ragione_sociale: "", fatt_piva: "", fatt_cf: "",
    fatt_indirizzo: "", fatt_cap: "", fatt_citta: "", fatt_provincia: "", fatt_nazione: "",
    fatt_email: "", fatt_pec: "", fatt_sdi: "",
  });
  const [sped, setSped] = useState({
    sped_destinatario: "", sped_indirizzo: "", sped_cap: "",
    sped_citta: "", sped_provincia: "", sped_nazione: "", sped_telefono: "", sped_note: "",
  });

  const [subtotal, setSubtotal] = useState(0);
  const [taxTotal, setTaxTotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [isPaid, setIsPaid] = useState(false);
  const [paidAt, setPaidAt] = useState(null);
  const [isPublished, setIsPublished] = useState(false);

  // collegamento ordine
  const [orderId, setOrderId] = useState(null);

  // fiscali
  const [docType, setDocType] = useState("TD01");
  const [regime, setRegime] = useState("RF01");
  const [naturaIva, setNaturaIva] = useState("N2.2");

  const [applyVat, setApplyVat] = useState(true);

  // Pubblica (modale + ricerca utente)
  const [showPub, setShowPub] = useState(false);
  const [recipientRole, setRecipientRole] = useState("");
  const [recipientUserId, setRecipientUserId] = useState("");

  // Typeahead
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("access")}` });
  const jsonHeaders = () => ({ ...headers(), "Content-Type": "application/json" });

  const load = async () => {
    setLoading(true);
    try {
      const me = await fetch(`${API_URL}/api/me/`, { headers: headers() })
        .then((r) => r.json())
        .catch(() => ({}));
      setRole((me?.role || "").toUpperCase());

      const res = await fetch(`${API_URL}/api/fatture/${id}/`, { headers: headers() });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail || "Errore caricamento");

      setStatus(d.status || "DRAFT");
      setSeries(d.series || "A");
      setNumber(d.number || null);
      setDate(d.date || "");
      setDueDate(d.due_date || "");
      setPaymentMethod(d.payment_method || "");

      setDocType((d.tipo_documento || "TD01").toUpperCase());
      setRegime((d.regime_fiscale || "RF01").toUpperCase());
      setNaturaIva((d.natura_iva || "N2.2").toUpperCase());

      const loadedItems = (d.items || []).map((r) => ({
        id: r.id,
        product: r.product,
        name: r.name,
        quantity: Number(r.quantity || 0),
        unit_price: Number(r.unit_price || 0),
        vat_rate: Number(r.vat_rate || 0),
        natura: r.natura || null,
        line_total: Number(r.line_total || 0),
      }));
      setItems(loadedItems);

      const allZeroVat = loadedItems.length > 0 && loadedItems.every((it) => Number(it.vat_rate || 0) === 0);
      setApplyVat(!allZeroVat ? true : false);

      const fattApi =
        d.fatturazione || {
          fatt_ragione_sociale: d.fatt_ragione_sociale,
          fatt_piva: d.fatt_piva,
          fatt_cf: d.fatt_cf,
          fatt_indirizzo: d.fatt_indirizzo,
          fatt_cap: d.fatt_cap,
          fatt_citta: d.fatt_citta,
          fatt_provincia: d.fatt_provincia,
          fatt_nazione: d.fatt_nazione,
          fatt_email: d.fatt_email,
          fatt_pec: d.fatt_pec,
          fatt_sdi: d.fatt_sdi,
        };
      const spedApi =
        d.spedizione || {
          sped_destinatario: d.sped_destinatario,
          sped_indirizzo: d.sped_indirizzo,
          sped_cap: d.sped_cap,
          sped_citta: d.sped_citta,
          sped_provincia: d.sped_provincia,
          sped_nazione: d.sped_nazione,
          sped_telefono: d.sped_telefono,
          sped_note: d.sped_note,
        };
      setFatt((prev) => ({ ...prev, ...Object.fromEntries(Object.entries(fattApi || {}).filter(([_, v]) => v !== undefined)) }));
      setSped((prev) => ({ ...prev, ...Object.fromEntries(Object.entries(spedApi || {}).filter(([_, v]) => v !== undefined)) }));

      setSubtotal(Number(d.subtotal || 0));
      setTaxTotal(Number(d.tax_total || 0));
      setTotal(Number(d.total || 0));
      setIsPaid(Boolean(d.is_paid));
      setPaidAt(d.paid_at || null);
      setIsPublished(Boolean(d.is_published));

      const oid = d.order_id ?? d.order?.id ?? d.order ?? null;
      setOrderId(oid || null);

      // Prefill destinazione (se presente sul documento)
      const prefRole = (d.origin_role || d.recipient_role || "").toUpperCase();
      if (prefRole) setRecipientRole(prefRole === "PV" ? "FILIALE" : prefRole);
      if (d.origin_user_id || d.recipient_user_id) {
        setRecipientUserId(String(d.origin_user_id || d.recipient_user_id));
      }

      // Prodotti
      const pRes = await fetch(`${API_URL}/api/prodotti/?tipo_listino=azienda`, { headers: headers() });
      const pDataRaw = await pRes.json().catch(() => []);
      const pData = Array.isArray(pDataRaw) ? pDataRaw : pDataRaw.results || [];
      setProducts(pData);
    } catch (e) {
      alert("❌ " + (e.message || "Errore"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const editingEnabled = status === "DRAFT";
  const setItem = (i, key, val) => setItems((arr) => arr.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));

  const onChangeProduct = (i, prodId) => {
    const p = products.find((x) => Number(x.id) === Number(prodId));
    setItems((arr) =>
      arr.map((r, idx) =>
        idx !== i
          ? r
          : {
              ...r,
              product: prodId || null,
              name: r.name || (p?.name || ""),
              unit_price: r.unit_price == null || Number(r.unit_price) === 0 ? defaultPriceFromProduct(p) : r.unit_price,
              vat_rate: !applyVat ? 0 : r.vat_rate == null || Number(r.vat_rate) === 0 ? defaultVatFromProduct(p) : r.vat_rate,
              natura: !applyVat ? naturaIva || null : null,
            }
      )
    );
  };

  const preview = useMemo(() => {
    let sub = 0, tax = 0;
    items.forEach((r) => {
      const q = toNumber(r.quantity, 0);
      const up = toNumber(r.unit_price, 0);
      const vr = toNumber(r.vat_rate, 0);
      const ls = q * up;
      sub += ls;
      tax += (ls * vr) / 100.0;
    });
    return { subtotal: sub, total: sub + tax, tax_total: tax };
  }, [items]);

  // ---------- SALVA ----------
  const handleSave = async () => {
    setSaving(true);
    try {
      const payloadRaw = {
        series,
        payment_method: paymentMethod,
        tipo_documento: (docType || "TD01").toUpperCase(),
        regime_fiscale: (regime || "RF01").toUpperCase(),
        natura_iva: naturaIva || undefined,
        date: toISODate(date) || undefined,
        due_date: toISODate(dueDate) || null,
        order: orderId || undefined,
        order_id: orderId || undefined,
        ...fatt,
        ...sped,
        items: (items || []).map((r) =>
          compact({
            id: r.id,
            product: r.product || null,
            name: r.name,
            quantity: toNumber(r.quantity, 0),
            unit_price: toNumber(r.unit_price, 0),
            vat_rate: toNumber(r.vat_rate, 0),
            natura: toNumber(r.vat_rate, 0) === 0 ? r.natura || naturaIva || null : null,
          })
        ),
      };
      const payload = compact(payloadRaw);

      const res = await fetch(`${API_URL}/api/fatture/${id}/`, {
        method: "PATCH",
        headers: jsonHeaders(),
        body: JSON.stringify(payload),
      });
      const t = await res.text();
      let d;
      try { d = JSON.parse(t); } catch { d = t; }
      if (!res.ok) {
        console.error("PATCH /api/fatture error:", d);
        throw new Error((d && d.detail) || "Errore salvataggio");
      }
      await load();
      alert("✅ Salvato");
    } catch (e) {
      alert("❌ " + (e.message || "Errore salvataggio"));
    } finally {
      setSaving(false);
    }
  };

  // controlli minimi prima di emettere (assegna numero)
  const preIssueChecks = () => {
    if (!series) { alert("Imposta la serie documento."); return false; }
    if (!fatt.fatt_ragione_sociale) { alert("Inserisci la ragione sociale."); return false; }
    if (!applyVat && !naturaIva) {
      alert("Se non applichi IVA, seleziona una Natura IVA (es. N2.2).");
      return false;
    }
    return true;
  };

  const emetti = async () => {
    if (!preIssueChecks()) return;
    try {
      const payload = { apply_vat: applyVat };

      if (recipientRole) {
        const roleToSend =
          recipientRole.toUpperCase() === "FILIALE" ? "PV" : recipientRole.toUpperCase();

        payload.recipient_role = roleToSend;
        payload.role = roleToSend; // compat

        if (recipientUserId && /^\d+$/.test(recipientUserId)) {
          payload.recipient_user_id = Number(recipientUserId);
          payload.user_id = Number(recipientUserId); // compat
        }
      }

      const res = await fetch(`${API_URL}/api/fatture/${id}/emetti/`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify(payload),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error("EMETTI 400:", d);
        throw new Error(d?.detail || "Errore emissione");
      }
      await load();
    } catch (e) {
      alert("❌ " + (e.message || "Errore emissione"));
    }
  };

  const inviaEmail = async () => {
    const to = prompt("Email destinatario (vuoto: usa email fatturazione)", "");
    try {
      const res = await fetch(`${API_URL}/api/fatture/${id}/invia-email/`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ to: to || undefined }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.detail || "Errore invio email");
      alert("📨 Email inviata");
      await load();
    } catch (e) {
      alert("❌ " + (e.message || "Errore invio"));
    }
  };

  // ---- Typeahead ricerca utenti per ruolo (accetta query vuota) ----
  const searchUsers = async (q) => {
    const r = (recipientRole || "").toUpperCase();
    if (!r) {
      setUserResults([]);
      return;
    }
    setLoadingUsers(true);
    try {
      const res = await fetch(
        `${API_URL}/api/users/search/?role=${encodeURIComponent(r)}&q=${encodeURIComponent((q || "").trim())}`,
        { headers: headers() }
      );
      const data = await res.json();
      setUserResults(Array.isArray(data) ? data : []);
    } catch {
      setUserResults([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Digiti → ricerca; cambi ruolo → ricerca (anche vuota)
  useEffect(() => {
    const t = setTimeout(() => searchUsers(userQuery), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userQuery, recipientRole]);

  const pubblica = async () => {
    if (!isMadre) return;

    // Se la proforma nasce da un ordine, pubblica automaticamente
    // lasciando al backend la derivazione del destinatario dall'ordine.
    if (orderId) {
      try {
        const res = await fetch(`${API_URL}/api/fatture/${id}/pubblica/`, {
          method: "POST",
          headers: jsonHeaders(),
          body: JSON.stringify(
            recipientUserId && /^\d+$/.test(recipientUserId)
              ? { recipient_user_id: Number(recipientUserId), user_id: Number(recipientUserId) }
              : {}
          ),
        });
        const d = await res.json().catch(() => ({}));
        if (!res.ok) {
          console.error("PUBBLICA AUTO 400:", d);
          throw new Error(d?.detail || d?.error || "Destinatario non impostato.");
        }
        alert("📤 Pubblicata nello spazio proforme");
        await load();
        setShowPub(false);
        return;
      } catch (e) {
        alert("❌ " + (e.message || "Errore pubblicazione"));
        return;
      }
    }

    // Per proforme manuali/bozze, manteniamo la scelta destinatario
    if (!recipientRole) {
      alert("Seleziona un ruolo destinatario prima di pubblicare.");
      setShowPub(true);
      return;
    }

    const roleToSend =
      (recipientRole || "").toUpperCase() === "FILIALE"
        ? "PV"
        : (recipientRole || "").toUpperCase();

    const payload = {
      recipient_role: roleToSend,
      role: roleToSend,
    };
    if (recipientUserId && /^\d+$/.test(recipientUserId)) {
      payload.recipient_user_id = Number(recipientUserId);
      payload.user_id = Number(recipientUserId);
    }

    try {
      const res = await fetch(`${API_URL}/api/fatture/${id}/pubblica/`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify(payload),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error("PUBBLICA 400:", d);
        throw new Error(d?.detail || d?.error || "Destinatario non impostato.");
      }
      alert("📤 Pubblicata nello spazio proforme");
      await load();
      setShowPub(false);
    } catch (e) {
      alert("❌ " + (e.message || "Errore pubblicazione"));
    }
  };

  const segnaPagata = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fatture/${id}/paga/`, { method: "POST", headers: headers() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.detail || "Errore");
      await load();
    } catch (e) {
      alert("❌ " + (e.message || "Errore segna pagata"));
    }
  };
  const segnaNonPagata = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fatture/${id}/non_paga/`, { method: "POST", headers: headers() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.detail || "Errore");
      await load();
    } catch (e) {
      alert("❌ " + (e.message || "Errore segna non pagata"));
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fatture/${id}/pdf/`, { headers: headers() });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.detail || "Errore PDF");
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `proforma_${series}_${number || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("❌ " + (e.message || "Errore PDF"));
    }
  };

  // link/righe da ordine & altre utility
  const linkOrder = async () => {
    const val = prompt("ID ordine da collegare a questa proforma:", orderId || "");
    const oid = val ? Number(val) : null;
    if (!oid) return;
    try {
      const or = await fetch(`${API_URL}/api/ordini/${oid}/`, { headers: headers() });
      if (!or.ok) {
        const d = await or.json().catch(() => ({}));
        throw new Error(d?.detail || "Ordine non trovato");
      }
      setOrderId(oid);
      await fetch(`${API_URL}/api/fatture/${id}/`, {
        method: "PATCH",
        headers: jsonHeaders(),
        body: JSON.stringify({ order: oid, order_id: oid }),
      });
      alert("🔗 Ordine collegato");
      await load();
    } catch (e) {
      alert("❌ " + (e.message || "Errore collegamento ordine"));
    }
  };

  const importFromOrder = async () => {
    const suggested = orderId || "";
    const val = prompt("Importa righe da ID ordine:", suggested);
    const oid = val ? Number(val) : null;
    if (!oid) return;

    try {
      const r = await fetch(`${API_URL}/api/ordini/${oid}/`, { headers: headers() });
      const o = await r.json();
      if (!r.ok) throw new Error(o?.detail || "Ordine non trovato");

      const mappedItems = (o.items || []).map((it) => {
        const prodId = it.product_id ?? it.product?.id ?? it.product ?? null;
        const pObj = products.find((pp) => Number(pp.id) === Number(prodId));
        const name = it.product_name || it.name || pObj?.name || "";
        const q = toNumber(it.quantity, 0);
        const up = toNumber(it.unit_price ?? defaultPriceFromProduct(pObj), 0);
        const vat = !applyVat ? 0 : toNumber(it.vat_rate ?? defaultVatFromProduct(pObj), 22);
        return { product: prodId, name, quantity: q, unit_price: up, vat_rate: vat, natura: vat === 0 ? naturaIva : null };
      });

      setItems(mappedItems);

      const fattOrd = o.fatturazione || {};
      const spedOrd = o.spedizione || {};
      setFatt((prev) => ({
        ...prev,
        ...Object.fromEntries(Object.entries(fattOrd).filter(([k, v]) => v && (prev[k] === "" || prev[k] == null))),
      }));
      setSped((prev) => ({
        ...prev,
        ...Object.fromEntries(Object.entries(spedOrd).filter(([k, v]) => v && (prev[k] === "" || prev[k] == null))),
      }));

      setOrderId(oid);
      await fetch(`${API_URL}/api/fatture/${id}/`, {
        method: "PATCH",
        headers: jsonHeaders(),
        body: JSON.stringify({ order: oid, order_id: oid }),
      });

      alert("✅ Righe importate dall’ordine. Ricorda di salvare.");
    } catch (e) {
      alert("❌ " + (e.message || "Errore importazione da ordine"));
    }
  };

  const deleteInvoice = async () => {
    if (!editingEnabled) return;
    if (!window.confirm("Eliminare definitivamente questa proforma in bozza?")) return;
    try {
      const r = await fetch(`${API_URL}/api/fatture/${id}/`, { method: "DELETE", headers: headers() });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d?.detail || "Errore eliminazione");
      }
      nav("/fatture");
    } catch (e) {
      alert("❌ " + (e.message || "Errore eliminazione"));
    }
  };

  if (loading) return <div className="container mt-4">Caricamento…</div>;

  const showPreviewTotals = status === "DRAFT";
  const dispSubtotal = showPreviewTotals ? preview.subtotal : subtotal;
  const dispTax = showPreviewTotals ? preview.tax_total : taxTotal;
  const dispTotal = showPreviewTotals ? preview.total : total;

  const showVatColumn = applyVat;

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={() => nav("/fatture")}>← Proforme</button>
          {orderId && (
            <button className="btn btn-outline-secondary" onClick={() => nav(`/ordini/${orderId}`)}>
              ← Ordine #{orderId}
            </button>
          )}
          <Link to="/" className="btn btn-outline-secondary">↩︎ Menu principale</Link>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button className="btn btn-outline-secondary" onClick={downloadPdf}>PDF</button>

          {status === "DRAFT" ? (
            <>
              <button className="btn btn-outline-primary" onClick={linkOrder}>🔗 Collega ordine…</button>
              <button className="btn btn-outline-success" onClick={importFromOrder}>⬇️ Importa da ordine…</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? "Salvataggio…" : "Salva"}
              </button>
              <button className="btn btn-success" onClick={emetti}>Emetti</button>
              <button className="btn btn-outline-danger" onClick={deleteInvoice}>Elimina</button>
            </>
          ) : (
            <>
              <button className="btn btn-outline-primary" onClick={inviaEmail}>Invia email</button>
              {isMadre && (
                  <>
                    <button className="btn btn-outline-success" onClick={pubblica}>Pubblica</button>
                </>
              )}
                {canManagePayments && (
                  !isPaid ? (
                    <button className="btn btn-warning" onClick={segnaPagata}>Segna pagata</button>
                  ) : (
                    <button className="btn btn-outline-warning" onClick={segnaNonPagata}>Segna NON pagata</button>
                  )
                )}
            </>
          )}
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center gap-2">
          {status !== "DRAFT" && (
            <div className="alert alert-warning d-flex align-items-center mb-2" role="alert">
              <span style={{fontSize: "18px", marginRight: "8px"}}>🔒</span>
              <div>
                <strong>Proforma bloccata</strong> — non modificabile
              </div>
            </div>
          )}
          <h2 className="m-0">Proforma {series}/{number || "—"}</h2>
          <span className={badge(status)}>{status}</span>
          {isPaid ? (
            <span className="badge bg-success">PAGATA {paidAt ? `(${formatDateTimeIT(paidAt)})` : ""}</span>
          ) : (
            <span className="badge bg-warning text-dark">NON PAGATA</span>
          )}
          {isPublished && (
            <span className="badge bg-primary">
              Pubblicata{recipientRole ? ` → ${recipientRole}${recipientUserId ? ` #${recipientUserId}` : ""}` : ""}
            </span>
          )}
          {orderId && <span className="badge bg-info text-dark">Ordine #{orderId}</span>}
          {!applyVat && <span className="badge bg-dark">Senza IVA (forfettario)</span>}
          <span className="badge bg-light text-dark">{docType}</span>
        </div>
      </div>

      <div className="row g-3">
        {/* Righe */}
        <div className="col-12 col-lg-8">
          <div className="card shadow-sm">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Righe</strong>
              {status === "DRAFT" && (
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() =>
                    setItems((arr) => [
                      ...arr,
                      { product: "", name: "", quantity: 1, unit_price: 0, vat_rate: applyVat ? 22 : 0, natura: applyVat ? null : naturaIva },
                    ])
                  }
                >
                  + Aggiungi riga
                </button>
              )}
            </div>
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ minWidth: 240 }}>Prodotto / Descrizione</th>
                    <th style={{ width: 90 }}>Qtà</th>
                    <th style={{ width: 120 }}>Prezzo</th>
                    {showVatColumn && <th style={{ width: 100 }}>IVA %</th>}
                    <th style={{ width: 120 }}>Totale</th>
                    {status === "DRAFT" && <th style={{ width: 60 }} />}
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={showVatColumn ? 6 : 5} className="text-muted py-4">Nessuna riga.</td>
                    </tr>
                  ) : (
                    items.map((r, i) => {
                      const prod = products.find((p) => Number(p.id) === Number(r.product));
                      const name = r.name || prod?.name || "";
                      const line =
                        toNumber(r.quantity, 0) *
                        toNumber(r.unit_price, 0) *
                        (1 + toNumber(r.vat_rate, 0) / 100);
                      return (
                        <tr key={i}>
                          <td>
                            <div className="d-flex gap-2">
                              <select
                                className="form-select"
                                disabled={!editingEnabled}
                                value={r.product || ""}
                                onChange={(e) => onChangeProduct(i, Number(e.target.value) || "")}
                              >
                                <option value="">—</option>
                                {products.map((p) => (
                                  <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                              </select>
                              <input
                                className="form-control"
                                placeholder="Descrizione"
                                disabled={!editingEnabled}
                                value={name}
                                onChange={(e) => setItem(i, "name", e.target.value)}
                              />
                            </div>
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control"
                              min="0"
                              step="1"
                              disabled={!editingEnabled}
                              value={r.quantity || 0}
                              onChange={(e) => setItem(i, "quantity", Number(e.target.value))}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control"
                              step="0.01"
                              disabled={!editingEnabled}
                              value={r.unit_price || 0}
                              onChange={(e) => setItem(i, "unit_price", Number(e.target.value))}
                            />
                          </td>
                          {showVatColumn && (
                            <td>
                              <input
                                type="number"
                                className="form-control"
                                step="0.01"
                                disabled={!editingEnabled}
                                value={r.vat_rate || 0}
                                onChange={(e) => setItem(i, "vat_rate", Number(e.target.value))}
                              />
                            </td>
                          )}
                          <td>€ {Number(line || 0).toFixed(2)}</td>
                          {editingEnabled && (
                            <td className="text-end">
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => setItems((arr) => arr.filter((_, x) => x !== i))}
                              >
                                ✕
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Riepilogo + dati */}
        <div className="col-12 col-lg-4">
          <div className="card shadow-sm mb-3">
            <div className="card-header"><strong>Riepilogo</strong></div>
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <span>Subtotale</span><strong>€ {dispSubtotal.toFixed(2)}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span>IVA</span><strong>€ {dispTax.toFixed(2)}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span>Totale</span><strong>€ {dispTotal.toFixed(2)}</strong>
              </div>
            </div>
          </div>

          {/* Impostazioni */}
          <div className="card shadow-sm mb-3">
            <div className="card-header"><strong>Impostazioni</strong></div>
            <div className="card-body">
              <div className="row g-2">
                <div className="col-4">
                  <label className="form-label">Serie</label>
                  <input className="form-control" value={series} disabled={!editingEnabled} onChange={(e) => setSeries(e.target.value)} />
                </div>
                <div className="col-8">
                  <label className="form-label">Data</label>
                  <input type="date" className="form-control" value={date || ""} disabled={!editingEnabled} onChange={(e) => setDate(e.target.value)} />
                </div>
                <div className="col-12">
                  <label className="form-label">Scadenza</label>
                  <input type="date" className="form-control" value={dueDate || ""} disabled={!editingEnabled} onChange={(e) => setDueDate(e.target.value)} />
                </div>
                <div className="col-12">
                  <label className="form-label">Metodo pagamento</label>
                  <input className="form-control" value={paymentMethod} disabled={!editingEnabled} onChange={(e) => setPaymentMethod(e.target.value)} />
                </div>
              </div>
            </div>
          </div>

          {/* Impostazioni fiscali */}
          <div className="card shadow-sm mb-3">
            <div className="card-header"><strong>Impostazioni fiscali</strong></div>
            <div className="card-body">
              <div className="row g-2">
                <div className="col-6">
                  <label className="form-label">Tipo Documento</label>
                  <select className="form-select" value={docType} disabled={!editingEnabled} onChange={(e) => setDocType(e.target.value)}>
                    {DOC_TYPES.map(o => <option key={o.v} value={o.v}>{o.l} ({o.v})</option>)}
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label">Regime Fiscale</label>
                  <select
                    className="form-select"
                    value={regime}
                    disabled={!editingEnabled}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRegime(v);
                      if (v === "RF19") {
                        setApplyVat(false);
                        setItems(arr => arr.map(r => ({ ...r, vat_rate: 0, natura: naturaIva || "N2.2" })));
                      }
                    }}
                  >
                    {REGIMI.map(o => <option key={o.v} value={o.v}>{o.l} ({o.v})</option>)}
                  </select>
                </div>

                <div className="col-12">
                  <div className="form-check mt-1">
                    <input
                      id="applyVat"
                      type="checkbox"
                      className="form-check-input"
                      disabled={!editingEnabled}
                      checked={applyVat}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setApplyVat(on);
                        setItems((arr) =>
                          arr.map((r) => {
                            if (on) {
                              const p = products.find((pp) => Number(pp.id) === Number(r.product));
                              const newVat = r.vat_rate && r.vat_rate > 0 ? r.vat_rate : defaultVatFromProduct(p);
                              return { ...r, vat_rate: newVat, natura: null };
                            }
                            return { ...r, vat_rate: 0, natura: naturaIva || "N2.2" };
                          })
                        );
                      }}
                    />
                    <label className="form-check-label" htmlFor="applyVat">
                      Applica IVA (disattiva per forfettario)
                    </label>
                  </div>
                </div>

                <div className="col-12">
                  <label className="form-label">Natura IVA (righe con IVA = 0)</label>
                  <select
                    className="form-select"
                    value={naturaIva}
                    disabled={!editingEnabled}
                    onChange={(e) => {
                      const val = e.target.value;
                      setNaturaIva(val);
                      if (!applyVat) {
                        setItems(arr => arr.map(r => toNumber(r.vat_rate,0)===0 ? ({ ...r, natura: val }) : r));
                      }
                    }}
                  >
                    {NATURE_IVA.map(o => <option key={o.v} value={o.v}>{o.l}{o.v ? ` (${o.v})` : ""}</option>)}
                  </select>
                  {!applyVat && <small className="text-muted">Per forfettario suggerito: N2.2</small>}
                </div>

                <div className="col-6">
                  <label className="form-label">Codice Destinatario (SDI)</label>
                  <input className="form-control" value={fatt.fatt_sdi || ""} disabled={!editingEnabled}
                         onChange={(e) => setFatt({ ...fatt, fatt_sdi: e.target.value })}/>
                </div>
                <div className="col-6">
                  <label className="form-label">PEC Destinatario</label>
                  <input className="form-control" value={fatt.fatt_pec || ""} disabled={!editingEnabled}
                         onChange={(e) => setFatt({ ...fatt, fatt_pec: e.target.value })}/>
                </div>
              </div>
            </div>
          </div>

          {/* Destinatario per Pubblica */}
          <div className="card shadow-sm mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Pubblicazione</strong>
              {isMadre && status !== "DRAFT" && (
                <button className="btn btn-sm btn-outline-success" onClick={() => {
                  setShowPub(true);
                  if (recipientRole) setTimeout(() => searchUsers(""), 0);
                }}>
                  Imposta & Pubblica…
                </button>
              )}
            </div>
            <div className="card-body">
              <div className="text-muted small mb-2">Destinazione attuale</div>
              <div>
                Ruolo: <strong>{recipientRole || "—"}</strong>{" "}
                {recipientUserId ? <>• Utente ID: <strong>{recipientUserId}</strong></> : null}
              </div>
              {!isMadre && <div className="form-text">Solo l’utente MADRE può pubblicare.</div>}
            </div>
          </div>

          {/* Intestazione & Spedizione */}
          <div className="card shadow-sm">
            <div className="card-header"><strong>Intestazione & Spedizione</strong></div>
            <div className="card-body">
              <label className="form-label">Ragione sociale</label>
              <input className="form-control mb-2" value={fatt.fatt_ragione_sociale || ""} disabled={!editingEnabled} onChange={(e) => setFatt({ ...fatt, fatt_ragione_sociale: e.target.value })} />
              <div className="row g-2">
                <div className="col-6">
                  <input className="form-control" placeholder="P.IVA" value={fatt.fatt_piva || ""} disabled={!editingEnabled} onChange={(e) => setFatt({ ...fatt, fatt_piva: e.target.value })} />
                </div>
                <div className="col-6">
                  <input className="form-control" placeholder="C.F." value={fatt.fatt_cf || ""} disabled={!editingEnabled} onChange={(e) => setFatt({ ...fatt, fatt_cf: e.target.value })} />
                </div>
              </div>
              <input className="form-control mt-2" placeholder="Email" value={fatt.fatt_email || ""} disabled={!editingEnabled} onChange={(e) => setFatt({ ...fatt, fatt_email: e.target.value })} />
            </div>
          </div>
        </div>
      </div>

      {status === "DRAFT" && (
        <div className="text-end mt-3">
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Salvataggio…" : "Salva modifiche"}
          </button>
        </div>
      )}

      {/* MODALE PUBBLICA con ricerca */}
      {showPub && isMadre && (
        <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: "rgba(0,0,0,.45)", zIndex: 1050 }} onClick={() => setShowPub(false)}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Pubblica nel portale destinatario</h5>
                <button className="btn-close" onClick={() => setShowPub(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Ruolo destinatario</label>
                  <select
                    className="form-select"
                    value={recipientRole}
                    onChange={(e) => {
                      setRecipientRole(e.target.value);
                      setRecipientUserId("");
                      setUserQuery("");
                      setUserResults([]);
                      setTimeout(() => searchUsers(""), 0); // carica subito i primi 50
                    }}
                  >
                    <option value="">— Seleziona —</option>
                    {ROLE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-2">
                  <label className="form-label">Utente (opzionale)</label>
                  <input
                    className="form-control"
                    placeholder={recipientRole ? "Cerca per nome / username / email…" : "Seleziona prima un ruolo"}
                    value={userQuery}
                    onChange={(e) => {
                      setUserQuery(e.target.value);
                      if (!e.target.value) setRecipientUserId("");
                    }}
                    disabled={!recipientRole}
                    autoComplete="off"
                  />
                </div>

                {!!recipientRole && (
                  <div className="list-group mb-2" style={{ maxHeight: 220, overflowY: "auto", borderRadius: 8 }}>
                    {loadingUsers ? (
                      <div className="list-group-item text-muted">Ricerca…</div>
                    ) : userResults.length === 0 ? (
                      <div className="list-group-item text-muted">Nessun utente trovato</div>
                    ) : (
                      userResults.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                            String(recipientUserId) === String(u.id) ? "active" : ""
                          }`}
                          onClick={() => {
                            setRecipientUserId(String(u.id));
                            setUserQuery(`${u.display_name || u.username}${u.email ? " · " + u.email : ""}`);
                          }}
                        >
                          <span>{u.display_name || u.username}</span>
                          <small className="text-muted">{u.email || ""}</small>
                        </button>
                      ))
                    )}
                  </div>
                )}

                <div className="form-text">
                  Lascia vuoto il campo utente per pubblicare a <strong>tutti</strong> gli utenti del ruolo.
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setShowPub(false)}>Annulla</button>
                <button className="btn btn-success" onClick={pubblica}>Pubblica</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
